import { css } from "@emotion/react";

export const backButtonStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px; /* Match Ant Segmented default height */
  padding: 0 12px;
  border-radius: 999px; /* pill */
  background-color: #2f2f2f; /* dark-friendly gray */
  color: #f3f3f3; /* white-ish icon */
  border: 1px solid #4a4a4a; /* subtle border */
  cursor: pointer;
  margin-right: 10px;
  margin-left: 5px;
  font-size: 16px; /* icon size */
  line-height: 1;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: #3a3a3a;
    border-color: #5a5a5a;
    color: #ffffff;
  }

  &:active {
    background-color: #262626;
    border-color: #555;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.08);
  }

  .anticon {
    margin-right: 0; /* icon-only */
  }
`;

export const topGridContainer = (
  isWebExtension: boolean,
  isMobile: boolean,
  chatPaneOpen: boolean
) =>
  css({
    display: "grid",
    gridTemplateRows: "3.5rem auto min-content",
    // Use a robust viewport height variable to handle mobile browser UI chrome
    minHeight: "calc(0.98 * var(--app-100vh))",
    position: "relative",
    width: isWebExtension
      ? "100%"
      : isMobile
      ? "min(75vw, 1200px)"
      : "min(70vw, 800px)",
    margin: "0 auto",
    overflow: chatPaneOpen ? "hidden" : "auto",
    height: chatPaneOpen ? "var(--app-100vh)" : "auto",
    "@media (max-width: 769px)": {
      width: isWebExtension ? "100%" : "99vw",
      // Subtract mobile nav height when chat pane is open, Match the MOBILE_NAV_HEIGHT from NavBar.tsx
      height:
        chatPaneOpen && !isWebExtension
          ? "calc(var(--app-100vh) - var(--mobile-nav-height))"
          : "auto",
    },
  });

export const flexNodeAndChatContainer = css({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  wordBreak: "break-word",
  paddingBottom: "55px",
  "@media (max-width: 769px)": {
    paddingBottom: "20px",
  },
});

export const mainNodesPaneContainer = (isWebExtension: boolean) =>
  css({
    maxHeight: "fit-content",
    padding: isWebExtension ? "0 10px" : "0",
  });

const bottomRowBorderRadius = "14px";
const chatPaneContainerBorder = "1px solid gray";

export const bottomRowContainer = (isWebExtension: boolean) =>
  css({
    position: "fixed",
    bottom: "max(var(--keyboard-inset), env(safe-area-inset-bottom))",
    backgroundColor: "#101010",
    width: "inherit",
    maxHeight: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    overscrollBehaviorY: "contain",
    borderLeft: chatPaneContainerBorder,
    borderRight: chatPaneContainerBorder,
    borderTop: chatPaneContainerBorder,
    borderBottomRightRadius: bottomRowBorderRadius,
    borderBottomLeftRadius: bottomRowBorderRadius,
    zIndex: 1000, // Add high z-index to ensure bottom row appears above other content
    "@media (max-width: 768px)": {
      // Match the MOBILE_NAV_HEIGHT from NavBar.tsx (only when not in web extension) and add keyboard inset
      ...(isWebExtension
        ? {}
        : {
            bottom:
              "max(var(--mobile-nav-height), max(var(--keyboard-inset), env(safe-area-inset-bottom)))",
          }),
    },
  });

export const pullUpDownControlContainer = css({
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  width: "100%",
  height: "5px",
});

export const pullUpDownControl = css({
  position: "relative",
  width: "38px",
  height: "17px",
  marginTop: "-15px",
  backgroundColor: "#101010",
  borderTop: chatPaneContainerBorder,
  borderTopLeftRadius: "15px",
  borderTopRightRadius: "15px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#888",
  fontSize: "10px",
  transition: "color 0.3s ease",
  "&:hover": {
    color: "#fff",
  },
});

export const chatPaneContainer = css({
  position: "relative",
  zIndex: 1000,
  overflow: "hidden",
  transition: "max-height 0.7s ease, height 0.7s ease",
  height: "1px",
  padding: "1px 1rem",
  borderRadius: "7px",
  borderBottomRightRadius: 0,
  borderBottomLeftRadius: 0,
  backgroundColor: "#101010",
});

export const chatPaneContainerOpen = (topRowBottom: number) =>
  css({
    height: `calc(var(--app-100vh) - ${topRowBottom}px - 60px)`,
    maxHeight: `calc(var(--app-100vh) - ${topRowBottom}px - 60px)`,
    overflowY: "auto",
    overscrollBehavior: "contain", // stop scroll chaining
    "@media (max-width: 768px)": {
      // Account for both the textarea height and mobile nav ( MOBILE_NAV_HEIGHT from NavBar.tsx )
      height: `calc(var(--app-100vh) - ${topRowBottom}px - calc(60px + var(--mobile-nav-height)))`,
      maxHeight: `calc(var(--app-100vh) - ${topRowBottom}px - calc(60px + var(--mobile-nav-height)))`,
    },
  });

export const textAreaTopContainer = css({
  height: "max-content",
  borderBottom: chatPaneContainerBorder,
  borderRadius: bottomRowBorderRadius,
  borderTopRightRadius: 0,
  borderTopLeftRadius: 0,
  backgroundColor: "#101010",
});

export const textAreaInnerContainer = css({
  display: "grid",
  gridTemplateColumns: "90% auto",
  // height: "max-content",
  // Give the parent a border that matches the "look" of the former textarea border
  border: chatPaneContainerBorder,
  borderRadius: bottomRowBorderRadius,
  // Add some padding to allow space for the send button on the right
  padding: "2px",
  backgroundColor: "#101010",
  margin: "0 auto 5px auto",
  width: "90%",
});

export const textAreaInnerContainerOnActionPopover = css({
  display: "grid",
  gridTemplateColumns: "80% auto",
  margin: "0 auto 3px auto",
  padding: "1px 2px",
});

export const textareaInputBar = css({
  resize: "none",
  width: "100%",
  display: "block",
  border: "none",
  borderRadius: "10px",
  outline: "none",
  margin: "8px auto",
  backgroundColor: "transparent",
  color: "#fff",
  fontSize: "16px",
  paddingLeft: "6px",
  "-webkit-text-size-adjust": "100%",
  "-webkit-font-smoothing": "antialiased",
});

export const textareaInputBarOnActionPopover = css({
  minWidth: "70px",
  margin: "4px auto",
  alignSelf: "center",
});

export const textareaSendButton = css(`
  display: block;
  width: 90%;
  margin: 2px auto;
  background-color: #404040;
  opacity: 0.8;
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 4px 2px;
  height: fit-content;
  align-self: center;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: 500;

  &:hover: {
    background-color: #4f4f4f,
  }
`);

export const nodeOutline = css({
  border: "1px solid red",
});

export const timeSource = css({
  color: "#3ea7ff",
  borderBottom: "1px solid #36a3ff",
  borderTop: "1px solid #36a3ff",
  borderLeft: "1px solid #36a3ff",
  borderRight: "1px solid #36a3ff",
  borderRadius: "6px",
  paddingTop: "2px",
  paddingBottom: "2px",
  paddingLeft: "4px",
  paddingRight: "4px",

  "&:hover": css({
    color: "#1d72b9",
    cursor: "pointer",
  }),
});
